/**
 * Interior Scope Sheet API — /Stage/interior/...
 * Auth: Cognito ID token (same pattern as interiorQuotationApi).
 * v1: site membership only — no interior.scope.* permission gates.
 *
 * Dev: `/Stage` via setupProxy.js (avoids CORS; API only allows app.innoworx.ai).
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { interiorQuotationApi } from '../InteriorQuotation/interiorQuotationApi';
import {
  PROGRAM_NUMBER_IDS,
  SINGLE_SELECT_ANSWER_IDS,
  coerceAnswerNumberValue,
  coerceAnswerNumbers,
  coerceProgramNumbers,
  coerceScopeWriteBody,
  coerceToApiNumber,
  isApiNumberFieldId,
  isStringNotNumberFieldId,
  isValidSubmitNumber,
  listNonNumberProgramFields,
  normalizeSingleSelectAnswers,
} from './scopeNumberCoercion';
import {
  finalizeAnswersForRoomType,
  isForbiddenAnswerForRoomType,
} from './scopeTemplateEngine';
import { emptyTipTapDoc, emptyTipTapNotes } from './tipTapEmpty';

export { emptyTipTapDoc, emptyTipTapNotes } from './tipTapEmpty';

const DEFAULT_API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const API_BASE_URL =
  process.env.REACT_APP_INTERIOR_SCOPE_API_BASE_URL ||
  (process.env.NODE_ENV === 'development' ? '/Stage' : DEFAULT_API_BASE);

const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('No authentication token available');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken.trim()}`,
    },
  };

  if (body != null && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(url, options);
  } catch (fetchError) {
    const error = new Error(fetchError.message || 'Network error');
    error.isNetworkError = true;
    throw error;
  }

  const responseText = await response.text().catch(() => '');
  let data = {};
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch {
      data = { message: responseText };
    }
  }

  if (!response.ok) {
    const message =
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.detail === 'string' && data.detail) ||
      (typeof data?.error === 'string' && data.error) ||
      responseText ||
      response.statusText ||
      'Unknown error';
    const error = new Error(
      typeof message === 'string' ? message : JSON.stringify(message)
    );
    error.status = response.status;
    error.responseData = data;
    error.code = data?.error || data?.code || null;
    error.missing = data?.missing || null;
    throw error;
  }

  return data;
};

const scopesBase = (siteId) =>
  `/interior/sites/${encodeURIComponent(siteId)}/scopes`;

const normalizeScope = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    scopeId: raw.scope_id || raw.scopeId || raw.id || '',
    siteId: raw.site_id || raw.siteId || '',
    tenantId: raw.tenant_id || raw.tenantId || '',
    templateId: raw.template_id || raw.templateId || 'ultimate-scope',
    templateVersion: raw.template_version || raw.templateVersion || '',
    status: raw.status || 'draft',
    // Keep server types as-is so we can detect "" / "0" vs real numbers.
    // Write path coerces; UI patches also coerce on change.
    program: raw.program || {},
    areas: raw.areas || {},
    rooms: Array.isArray(raw.rooms) ? raw.rooms : [],
    documentNotes: raw.document_notes || raw.documentNotes || emptyTipTapNotes(),
    createdAt: raw.created_at || raw.createdAt || '',
    updatedAt: raw.updated_at || raw.updatedAt || '',
    raw,
  };
};

/** CloudFront has no /scopes* behaviors yet — REST returns 403 instead of API 401. */
export const isScopesStageUnavailable = (err) =>
  Boolean(err && (err.status === 403 || err.status === 404));

export const formatScopeApiError = (err) => {
  if (!err) return 'Something went wrong';
  if (err.isNetworkError) {
    return 'Network error — check connection or API availability';
  }
  const data = err.responseData || {};
  if (isScopesStageUnavailable(err)) {
    return (
      'Scope Sheet API is not live on Stage yet (CloudFront has no /scopes or ' +
      '/scope-templates routes). Backend must deploy edge-delivery + ' +
      'interior-scopes DynamoDB + SCOPES_TABLE on innoworx-interior-api.'
    );
  }
  if (err.status === 409 && data.error === 'truncate_requires_confirm') {
    return 'Reducing room counts will remove existing room data. Confirm to continue.';
  }
  if (err.status === 409) {
    return data.message || 'This scope cannot be edited in its current status.';
  }

  const detailParts = [];
  if (Array.isArray(data.missing) && data.missing.length) {
    detailParts.push(
      ...data.missing
        .map((m) => {
          const base = m.label || m.question_id || m.section || m.message;
          if (!base) return null;
          if (m.got && !String(base).includes(String(m.got))) {
            return `${base} [${m.got}]`;
          }
          return base;
        })
        .filter(Boolean)
    );
  }
  if (Array.isArray(data.invalid) && data.invalid.length) {
    detailParts.push(
      ...data.invalid
        .map(
          (m) =>
            m.message ||
            (m.question_id
              ? `${m.question_id} invalid option`
              : null) ||
            m.label
        )
        .filter(Boolean)
    );
  }
  if (Array.isArray(data.details) && data.details.length) {
    detailParts.push(
      ...data.details
        .map((m) =>
          typeof m === 'string'
            ? m
            : m.message || m.question_id || JSON.stringify(m)
        )
        .filter(Boolean)
    );
  }
  if (detailParts.length) {
    if (
      typeof err.message === 'string' &&
      /not storing (program |area )? ?(JSON )?numbers|Area numbers not stored|Cannot submit —|Program numbers cleared|cleared program number types|failed to build number payload|Empty dimensions may be skipped/i.test(
        err.message
      )
    ) {
      return err.message;
    }
    return `Validation failed: ${detailParts.slice(0, 8).join(', ')}`;
  }

  const msg =
    (typeof data.message === 'string' && data.message) ||
    (typeof data.detail === 'string' && data.detail) ||
    (typeof data.error === 'string' &&
    data.error !== 'validation_failed'
      ? data.error
      : null) ||
    err.message ||
    'Request failed';
  if (data.error === 'validation_failed' && msg && !/^validation/i.test(msg)) {
    return `Validation failed: ${msg}`;
  }
  if (msg === 'validation_failed') {
    return 'Validation failed — check required fields and choice options.';
  }
  return msg;
};

/** Unwrap { template: {...} } from GET /scope-templates/ultimate. */
export const normalizeUltimateTemplate = (payload) => {
  const template = payload?.template || payload;
  if (!template || typeof template !== 'object') return null;
  return {
    id: template.id || 'ultimate-scope',
    version: template.version || '1.0.0',
    title: template.title || 'The Ultimate Scope Sheet',
    source: template.source || '',
    programFields: Array.isArray(template.program_fields)
      ? template.program_fields
      : [],
    optionSets: template.option_sets || {},
    questionGroups: template.question_groups || {},
    roomTemplates: Array.isArray(template.room_templates)
      ? template.room_templates
      : [],
    documentNotes: template.document_notes || null,
    raw: template,
  };
};

export const interiorScopeApi = {
  getSites: () => interiorQuotationApi.getSites(),

  getLatestTemplate: async () => {
    const data = await makeApiRequest(
      '/interior/scope-templates/ultimate',
      'GET'
    );
    return normalizeUltimateTemplate(data);
  },

  getTemplateVersion: async (version) => {
    const data = await makeApiRequest(
      `/interior/scope-templates/ultimate/${encodeURIComponent(version)}`,
      'GET'
    );
    return normalizeUltimateTemplate(data);
  },

  listScopes: async (siteId) => {
    const data = await makeApiRequest(scopesBase(siteId), 'GET');
    const list = Array.isArray(data)
      ? data
      : data?.scopes || data?.items || data?.data || [];
    return list.map(normalizeScope).filter(Boolean);
  },

  createScope: async (siteId, body = {}) => {
    const data = await makeApiRequest(scopesBase(siteId), 'POST', body);
    return normalizeScope(data?.scope || data);
  },

  getScope: async (siteId, scopeId) => {
    const data = await makeApiRequest(
      `${scopesBase(siteId)}/${encodeURIComponent(scopeId)}`,
      'GET'
    );
    return normalizeScope(data?.scope || data);
  },

  patchScope: async (siteId, scopeId, body) => {
    // Strip FE-invented / forbidden question ids from every area before PATCH.
    // Never send null for unknown ids — Stage returns 400 unknown question_id.
    const next = { ...(body || {}) };
    if (next.areas && typeof next.areas === 'object') {
      const areas = {};
      Object.entries(next.areas).forEach(([roomType, area]) => {
        areas[roomType] = {
          ...(area || {}),
          room_type: roomType,
          answers: finalizeAnswersForRoomType(roomType, area?.answers || {}),
        };
      });
      next.areas = areas;
    }
    if (Array.isArray(next.rooms)) {
      next.rooms = next.rooms.map((room) => {
        if (!room || typeof room !== 'object') return room;
        return {
          ...room,
          answers: finalizeAnswersForRoomType(
            room.room_type,
            room.answers || {}
          ),
        };
      });
    }

    const data = await makeApiRequest(
      `${scopesBase(siteId)}/${encodeURIComponent(scopeId)}`,
      'PATCH',
      coerceScopeWriteBody(next)
    );
    return normalizeScope(data?.scope || data) || data;
  },

  expandScope: async (siteId, scopeId, body) => {
    const data = await makeApiRequest(
      `${scopesBase(siteId)}/${encodeURIComponent(scopeId)}/expand`,
      'POST',
      coerceScopeWriteBody(body)
    );
    return normalizeScope(data?.scope || data) || data;
  },

  /**
   * Write JSON numbers in two steps (program, then areas/rooms), then verify.
   * IMPORTANT: the last PATCH before return/submit must be areas/rooms when
   * possible — a trailing program-only PATCH clears area number types on Stage.
   */
  ensureProgramNumbers: async (
    siteId,
    scopeId,
    program = {},
    areas = null,
    rooms = null
  ) => {
    const endpoint = `${scopesBase(siteId)}/${encodeURIComponent(scopeId)}`;
    const coercedProgram = coerceProgramNumbers(program);

    const buildAreaRoomBody = (serverAreas = null, serverRooms = null) => {
      const areaRoomBody = {};
      // Always rebuild from the latest server areas so main_entrance / etc.
      // cannot be dropped if the caller omitted a key.
      const areaSource = {};
      Object.entries(serverAreas || {}).forEach(([roomType, area]) => {
        areaSource[roomType] = {
          ...(area || {}),
          answers: { ...(area?.answers || {}) },
        };
      });
      Object.entries(areas || {}).forEach(([roomType, area]) => {
        const prev = areaSource[roomType] || {};
        areaSource[roomType] = {
          ...prev,
          ...(area || {}),
          room_type: roomType,
          answers: {
            ...(prev.answers || {}),
            ...(area?.answers || {}),
          },
        };
      });

      if (Object.keys(areaSource).length) {
        const nextAreas = {};
        Object.entries(areaSource).forEach(([roomType, area]) => {
          const answers = coerceAnswerNumbers(
            normalizeSingleSelectAnswers(area?.answers || {})
          );
          Object.keys(answers).forEach((id) => {
            if (isForbiddenAnswerForRoomType(roomType, id)) {
              delete answers[id];
              return;
            }
            if (SINGLE_SELECT_ANSWER_IDS.has(id)) return;
            if (isApiNumberFieldId(id) && !isStringNotNumberFieldId(id)) {
              answers[id] = coerceAnswerNumberValue(answers[id]);
            }
          });
          const alwaysForce =
            roomType === 'main_entrance'
              ? [
                  'door_height',
                  'door_width',
                  'length',
                  'width',
                  'ceiling_height',
                  'ceiling_lights_qty',
                ]
              : roomType === 'living_drawing'
                ? [
                    'length',
                    'width',
                    'ceiling_height',
                    'lintel_height',
                    'sill_height',
                    'window_count',
                    'lounge_chairs_qty',
                    'armchairs_qty',
                    'wall_lights_qty',
                    'fans_qty',
                    'ac_wiring_qty',
                    'sofa_seater',
                  ]
                : roomType === 'dining'
                  ? [
                      'length',
                      'width',
                      'ceiling_height',
                      'lintel_height',
                      'sill_height',
                      'window_count',
                      'wall_lights_qty',
                      'fans_qty',
                      'ac_wiring_qty',
                      'dining_chairs_qty',
                      'dining_table_persons',
                    ]
                  : roomType === 'foyer' || roomType === 'kitchen'
                    ? [
                        'length',
                        'width',
                        'ceiling_height',
                        'lintel_height',
                        'sill_height',
                        'window_count',
                        'wall_lights_qty',
                        'fans_qty',
                        'ac_wiring_qty',
                      ]
                    : [];
          alwaysForce.forEach((id) => {
            if (SINGLE_SELECT_ANSWER_IDS.has(id)) return;
            if (isForbiddenAnswerForRoomType(roomType, id)) return;
            answers[id] = coerceAnswerNumberValue(answers[id]);
          });
          // Drop FE-invented keys — never send null (Stage 400s unknown question_id)
          nextAreas[roomType] = {
            room_type: roomType,
            ...(area || {}),
            answers: finalizeAnswersForRoomType(
              roomType,
              normalizeSingleSelectAnswers(answers)
            ),
          };
        });
        areaRoomBody.areas = nextAreas;
      }

      const roomSource = [];
      const byId = new Map();
      (serverRooms || []).forEach((room) => {
        const id = room?.instance_id || room?.instanceId;
        if (id) byId.set(id, { ...room, answers: { ...(room.answers || {}) } });
      });
      (rooms || []).forEach((room) => {
        const id = room?.instance_id || room?.instanceId;
        if (!id) return;
        const prev = byId.get(id) || {};
        byId.set(id, {
          ...prev,
          ...room,
          instance_id: id,
          answers: {
            ...(prev.answers || {}),
            ...(room.answers || {}),
          },
        });
      });
      byId.forEach((room) => {
        const roomType = room.room_type;
        roomSource.push({
          instance_id: room.instance_id || room.instanceId,
          label: room.label,
          answers: finalizeAnswersForRoomType(
            roomType,
            coerceAnswerNumbers(
              normalizeSingleSelectAnswers(room.answers || {})
            )
          ),
        });
      });
      if (roomSource.length) areaRoomBody.rooms = roomSource;
      return areaRoomBody;
    };

    let rawScope = null;
    const readRaw = async () => {
      const after = await makeApiRequest(endpoint, 'GET');
      return after?.scope || after || {};
    };

    const patchAreasRooms = async () => {
      rawScope = await readRaw();
      const body = buildAreaRoomBody(rawScope.areas, rawScope.rooms);
      if (body.areas || body.rooms) {
        await makeApiRequest(endpoint, 'PATCH', body);
      }
    };

    const patchProgram = async () => {
      await makeApiRequest(endpoint, 'PATCH', {
        program: coercedProgram,
      });
    };

    // --- Step 1: program ---
    await patchProgram();
    try {
      await makeApiRequest(`${endpoint}/expand`, 'POST', {
        program: coercedProgram,
        confirm_truncate: false,
      });
    } catch (expandErr) {
      // ignore unchanged counts / truncate confirm
    }

    rawScope = await readRaw();
    let programFailures = listNonNumberProgramFields(rawScope.program || {});
    if (programFailures.length) {
      const forced = {};
      PROGRAM_NUMBER_IDS.forEach((key) => {
        forced[key] = coerceToApiNumber(
          coercedProgram[key] ?? rawScope.program?.[key]
        );
      });
      await makeApiRequest(endpoint, 'PATCH', { program: forced });
      rawScope = await readRaw();
      programFailures = listNonNumberProgramFields(rawScope.program || {});
      if (programFailures.length) {
        const sample = programFailures
          .slice(0, 3)
          .map((key) => {
            const v = rawScope.program?.[key];
            return `${key}=${typeof v}:${JSON.stringify(v)}`;
          })
          .join(', ');
        const err = new Error(
          `Server is not storing program numbers (${sample}). Backend must persist JSON number types.`
        );
        err.status = 400;
        err.code = 'validation_failed';
        err.responseData = {
          error: 'validation_failed',
          missing: programFailures.map((question_id) => ({
            section: 'program',
            question_id,
            label: `${question_id} must be a number`,
          })),
        };
        throw err;
      }
    }

    // --- Step 2: areas / rooms (must be last successful content write) ---
    await patchAreasRooms();

    // --- Step 3: if areas wiped program, restore program then re-write areas ---
    rawScope = await readRaw();
    programFailures = listNonNumberProgramFields(rawScope.program || {});
    if (programFailures.length) {
      await patchProgram();
      await patchAreasRooms(); // areas LAST
      rawScope = await readRaw();
      programFailures = listNonNumberProgramFields(rawScope.program || {});
      if (programFailures.length) {
        const err = new Error(
          'Could not keep both program and area number types saved. Try submit again.'
        );
        err.status = 400;
        err.code = 'validation_failed';
        err.responseData = {
          error: 'validation_failed',
          missing: programFailures.map((question_id) => ({
            section: 'program',
            question_id,
            label: `${question_id} must be a number`,
          })),
        };
        throw err;
      }
    }

    // --- Step 4: if key area dims are still not numbers, patch areas again ---
    const areaNumberOk = (section, id) => {
      const v = rawScope.areas?.[section]?.answers?.[id];
      return typeof v === 'number' && Number.isFinite(v);
    };
    const criticalAreaChecks = [
      ['main_entrance', 'door_height'],
      ['main_entrance', 'length'],
      ['living_drawing', 'lintel_height'],
      ['living_drawing', 'lounge_chairs_qty'],
      ['foyer', 'length'],
      ['kitchen', 'length'],
      ['dining', 'length'],
    ];
    const needsAreaRewrite = criticalAreaChecks.some(
      ([section, id]) =>
        rawScope.areas?.[section] && !areaNumberOk(section, id)
    );
    if (needsAreaRewrite) {
      await patchAreasRooms();
      rawScope = await readRaw();
      // If that cleared program, restore both with areas last
      if (listNonNumberProgramFields(rawScope.program || {}).length) {
        await patchProgram();
        await patchAreasRooms();
        rawScope = await readRaw();
      }
    }

    // --- Step 5: last-mile — force truthy numbers (Stage rejects falsy 0) ---
    rawScope = await readRaw();
    const criticalPatches = {
      main_entrance: [
        'door_height',
        'door_width',
        'length',
        'width',
        'ceiling_height',
        'ceiling_lights_qty',
      ],
      living_drawing: [
        'lintel_height',
        'lounge_chairs_qty',
        'sill_height',
        'sofa_seater',
        'length',
        'width',
        'ceiling_height',
        'window_count',
        'armchairs_qty',
        'wall_lights_qty',
        'fans_qty',
        'ac_wiring_qty',
      ],
    };

    for (const [roomType, ids] of Object.entries(criticalPatches)) {
      if (!rawScope.areas?.[roomType] && !areas?.[roomType]) continue;
      const existing = {
        ...(rawScope.areas?.[roomType]?.answers || {}),
        ...(areas?.[roomType]?.answers || {}),
      };
      const answers = coerceAnswerNumbers(existing);
      ids.forEach((id) => {
        if (isForbiddenAnswerForRoomType(roomType, id)) return;
        answers[id] = coerceAnswerNumberValue(existing[id]);
      });
      await makeApiRequest(
        endpoint,
        'PATCH',
        coerceScopeWriteBody({
          areas: {
            [roomType]: {
              room_type: roomType,
              answers: finalizeAnswersForRoomType(roomType, answers),
            },
          },
        })
      );
    }

    rawScope = await readRaw();

    // --- Step 6: verify area types ---
    const bad = [];
    Object.entries(criticalPatches).forEach(([roomType, ids]) => {
      if (!rawScope.areas?.[roomType]) return;
      ids.forEach((id) => {
        const got = rawScope.areas[roomType]?.answers?.[id];
        if (!isValidSubmitNumber(got)) {
          bad.push({
            section: roomType,
            question_id: id,
            label: `${id} must be a number`,
            got: `${typeof got}:${JSON.stringify(got)}`,
          });
        }
      });
    });
    if (bad.length) {
      const sample = bad
        .slice(0, 4)
        .map((m) => `${m.section}.${m.question_id}=${m.got}`)
        .join('; ');
      const err = new Error(
        `Area numbers not stored after PATCH (${sample}). Backend area-answer merge may be skipping empty values.`
      );
      err.status = 400;
      err.code = 'validation_failed';
      err.responseData = { error: 'validation_failed', missing: bad };
      throw err;
    }

    // --- Step 7: area-only PATCHes wipe program number types — restore both ---
    const patchCriticalAreas = async () => {
      for (const [roomType, ids] of Object.entries(criticalPatches)) {
        if (!rawScope.areas?.[roomType] && !areas?.[roomType]) continue;
        const existing = {
          ...(rawScope.areas?.[roomType]?.answers || {}),
          ...(areas?.[roomType]?.answers || {}),
        };
        const answers = coerceAnswerNumbers(existing);
        ids.forEach((id) => {
          if (isForbiddenAnswerForRoomType(roomType, id)) return;
          answers[id] = coerceAnswerNumberValue(existing[id]);
        });
        await makeApiRequest(
          endpoint,
          'PATCH',
          coerceScopeWriteBody({
            areas: {
              [roomType]: {
                room_type: roomType,
                answers: finalizeAnswersForRoomType(roomType, answers),
              },
            },
          })
        );
      }
      rawScope = await readRaw();
    };

    programFailures = listNonNumberProgramFields(rawScope.program || {});
    if (programFailures.length) {
      await patchProgram();
      await patchCriticalAreas();
      rawScope = await readRaw();
      programFailures = listNonNumberProgramFields(rawScope.program || {});
      // One more cycle if still broken
      if (programFailures.length) {
        await patchProgram();
        await patchCriticalAreas();
        rawScope = await readRaw();
        programFailures = listNonNumberProgramFields(rawScope.program || {});
      }
    }

    // Always finish with a clean program write then critical areas (areas last)
    await patchProgram();
    await patchCriticalAreas();
    rawScope = await readRaw();
    programFailures = listNonNumberProgramFields(rawScope.program || {});
    if (programFailures.length) {
      const sample = programFailures
        .slice(0, 3)
        .map((key) => {
          const v = rawScope.program?.[key];
          return `${key}=${typeof v}:${JSON.stringify(v)}`;
        })
        .join(', ');
      const err = new Error(
        `Program numbers cleared after area save (${sample}).`
      );
      err.status = 400;
      err.code = 'validation_failed';
      err.responseData = {
        error: 'validation_failed',
        missing: programFailures.map((question_id) => ({
          section: 'program',
          question_id,
          label: `${question_id} must be a number`,
          got: `${typeof rawScope.program?.[question_id]}:${JSON.stringify(rawScope.program?.[question_id])}`,
        })),
      };
      throw err;
    }

    // FE never re-sends FE_ONLY / forbidden keys (finalizeAnswersForRoomType).
    // Stage rejects unknown question_id even as null, so we omit rather than clear.

    return {
      scope: normalizeScope(rawScope),
      submitBody: {},
    };
  },

  submitScope: async (siteId, scopeId, body = {}) => {
    const payload =
      body && Object.keys(body).length ? coerceScopeWriteBody(body) : {};
    const data = await makeApiRequest(
      `${scopesBase(siteId)}/${encodeURIComponent(scopeId)}/submit`,
      'POST',
      payload
    );
    return normalizeScope(data?.scope || data) || data;
  },

  reopenScope: async (siteId, scopeId) => {
    const data = await makeApiRequest(
      `${scopesBase(siteId)}/${encodeURIComponent(scopeId)}/reopen`,
      'POST',
      {}
    );
    return normalizeScope(data?.scope || data) || data;
  },
};

export default interiorScopeApi;
